/* eslint-disable no-unused-vars */
import BigNumber from "bignumber.js";
import { useLeverageContext } from "context/InstantsLeverage/LeverageContext/useLeverageContext";
import useAllBorrowData from "hooks/contexts/BorrowContext/useAllBorrowData";
import { useDepositedAssetStates } from "hooks/contexts/BorrowContext/useDepositedAssetStates";
import { get } from "lodash";
import numeral from "numeral";
import { useMemo } from "react";

export const useLeveragePositionData = (leverageType) => {
  const [depositedAssetList] = useDepositedAssetStates();
  const { laveragedBorrowList } = useLeverageContext();
  const { lendingTokensObj, collateralTokenObj } = useAllBorrowData();

  const positionData = useMemo(() => {
    let totalExposureBN = new BigNumber(0);
    let totalEquityBN = new BigNumber(0);
    let totalProfitBN = new BigNumber(0);

    const positionList = depositedAssetList
      .filter((x) => x.isLeverage && x.leverageType === leverageType)
      .map((depositedAsset) => {
        const laveragedBorrow = laveragedBorrowList.find(
          (x) => x?.prjTokenAddress === depositedAsset?.address
        );

        const shortAsset = lendingTokensObj[depositedAsset?.lendingToken?.address]?.[0];
        const longAsset = collateralTokenObj[depositedAsset?.address]?.[0];

        const openingLongAssetPrice = laveragedBorrow?.prjTokenPrice || 0;

        const currentLongAssetPrice = Number(longAsset?.price).toFixed(6) || 0;
        const currentShortAssetPrice = Number(shortAsset?.price).toFixed(6) || 0;

        const currentPrice = new BigNumber(currentLongAssetPrice)
          .dividedBy(currentShortAssetPrice)
          .toString();

        const openingLongNotionalValue = laveragedBorrow?.exposureAmount;
        const openingShortAssetPrice = laveragedBorrow?.lendingTokenPrice || "0";

        const notionalShortTokenCount = new BigNumber(openingLongNotionalValue).dividedBy(
          openingShortAssetPrice
        );

        const notionalLongTokenCount = new BigNumber(openingLongNotionalValue).dividedBy(
          openingLongAssetPrice
        );

        const longMarginTokenCount = laveragedBorrow?.marginCount;
        const openingMarginValue = new BigNumber(openingLongAssetPrice).multipliedBy(
          longMarginTokenCount
        );

        const currentLongNotionalValue = notionalLongTokenCount.multipliedBy(currentLongAssetPrice);
        const currentMarginValue = new BigNumber(currentLongAssetPrice).multipliedBy(
          longMarginTokenCount
        );

        const currentShortNotionalValue = new BigNumber(currentShortAssetPrice).multipliedBy(
          notionalShortTokenCount
        );

        const shortLvr = get(shortAsset, "lvr", 0);
        const longLvr = get(longAsset, "lvr", 0);

        const totalLvr = shortLvr * longLvr;
        const totalLvrBN = new BigNumber(totalLvr);

        const loan = BigNumber(depositedAsset.data.totalOutstanding.decimal).multipliedBy(
          currentShortAssetPrice
        );
        const collateralBalance = depositedAsset.data.collateralBalance[2].rounded;

        // const currentLongAssetCount = openingMarginValue
        //   .plus(openingLongNotionalValue)
        //   .dividedBy(openingLongAssetPrice);

        const currentLongAssetCount = depositedAsset.data.collateralBalance[2].rounded;

        const currentLongAssetValue = new BigNumber(currentLongAssetPrice)
          .multipliedBy(currentLongAssetCount)
          .toString();

        const currentShortAssetValue = loan;

        // Safety Buffer
        const safetyBuffer = BigNumber(1).minus(
          loan.dividedBy(totalLvrBN.multipliedBy(collateralBalance))
        );

        // Liquidation Price
        const liquidationPrice = new BigNumber(currentLongAssetPrice)
          .multipliedBy(new BigNumber(1).minus(safetyBuffer))
          .toString();

        // APY
        const borrowApy = get(shortAsset, "borrowApy", 0);

        // Exposure
        const exposureUSD = new BigNumber(currentLongAssetValue)
          .dividedBy(currentLongAssetPrice)
          .toString();
        const exposure = new BigNumber(exposureUSD).dividedBy(currentLongAssetPrice).toString();

        // Equity
        // const equityUSD = currentLongNotionalValue
        //   .plus(currentMarginValue)
        //   .minus(currentShortNotionalValue);
        const equityUSD = BigNumber(exposureUSD).minus(currentShortAssetValue);
        const equity = equityUSD.dividedBy(currentLongAssetPrice);

        // Profit/Loss
        const currentExposureLongValue = notionalLongTokenCount.multipliedBy(currentLongAssetPrice);
        const currentExposureShortValue =
          notionalShortTokenCount.multipliedBy(currentShortAssetPrice);
        const profitUSD = currentExposureLongValue.minus(currentExposureShortValue);
        const profit = new BigNumber(profitUSD).dividedBy(currentLongAssetPrice);

        // Total data
        totalExposureBN = totalExposureBN.plus(exposureUSD);
        totalEquityBN = totalEquityBN.plus(equityUSD);
        totalProfitBN = totalProfitBN.plus(profitUSD);

        return {
          ...depositedAsset,
          longAsset: depositedAsset?.logo,
          shortAsset: depositedAsset?.lendingToken?.logo,
          pair: `${longAsset?.symbol}/${shortAsset?.symbol}`,
          decimal: longAsset?.decimal,
          asset: depositedAsset?.symbol,
          currentPrice: `${numeral(currentPrice).format("0,0.[00]")}`,
          price: currentPrice,
          liquidationPrice: `${numeral(liquidationPrice).format("0,0.[00]")}`,
          safetyBuffer: `${numeral(safetyBuffer || 0).format("0.00%")}`,
          healthFactor: depositedAsset.healthFactor,
          apy: numeral(borrowApy).format("0.00%"),
          exposureUSD: `$${numeral(exposureUSD).format("0,0.[00]")}`,
          exposure: `${numeral(exposure).format("0,0.[0000]")} ${depositedAsset?.symbol}`,
          equityUSD: `$${numeral(equityUSD).format("0,0.[00]")}`,
          equity: `${numeral(equity).format("0,0.[0000]")} ${depositedAsset?.symbol}`,
          profitUSD: `$${+numeral(profitUSD) ? numeral(profitUSD).format("0,0.[00]") : 0}`,
          profit: `${+numeral(profit) ? numeral(profit).format("0,0.[0000]") : 0} ${
            depositedAsset?.symbol
          }`,
        };
      });
    return {
      positionList,
      totalExposure: `$${numeral(totalExposureBN.toString()).format("0,0.[00]")}`,
      totalEquity: `$${numeral(totalEquityBN.toString()).format("0,0.[00]")}`,
      totalProfit: `$${
        +numeral(totalProfitBN.toString())
          ? numeral(totalProfitBN.toString()).format("0,0.[00]")
          : 0
      }`,
    };
  }, [depositedAssetList, leverageType, laveragedBorrowList, collateralTokenObj, lendingTokensObj]);

  return positionData;
};
